import { useMemo } from 'react';
import { getCategory } from '../utils/categoryUtils';

/**
 * Custom hook to handle data filtering based on search term and category
 * 
 * @param {Array} data - The data array to filter
 * @param {string} searchTerm - Text to search for in task names
 * @param {string} selectedCategory - Selected category for filtering
 * @param {Object} sortConfig - Current sort configuration
 * @returns {Array} - Filtered data array
 */
const useDataFiltering = (data, searchTerm, selectedCategory, sortConfig) => {
  // First sort the data based on current sort configuration
  const sortedData = useMemo(() => {
    if (!data) return [];
    
    let sortableData = [...data];
    
    if (sortConfig.key === 'id') {
      sortableData.sort((a, b) => {
        if (sortConfig.direction === 'ascending') {
          return a.id - b.id;
        }
        return b.id - a.id;
      });
    } else if (sortConfig.key === 'task') {
      sortableData.sort((a, b) => {
        if (sortConfig.direction === 'ascending') {
          return a.task.localeCompare(b.task);
        }
        return b.task.localeCompare(a.task);
      });
    } else {
      // Sorting by model score
      sortableData.sort((a, b) => {
        if (sortConfig.direction === 'ascending') {
          return a.scores[sortConfig.key] - b.scores[sortConfig.key];
        }
        return b.scores[sortConfig.key] - a.scores[sortConfig.key];
      });
    }
    
    return sortableData;
  }, [data, sortConfig]);

  // Then filter the sorted data by search term and category
  const filteredData = useMemo(() => {
    return sortedData.filter(item => {
      const matchesSearch = item.task.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || getCategory(item.task) === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [sortedData, searchTerm, selectedCategory]);

  return filteredData;
};

export default useDataFiltering;
